<template>
    <header
        class="primary-navigation"
        id="universal_header"
        :aria-label="$i18n('accessibility.sections.uh_nav_header')"
    >
        <div class="header-container universal-header-dark">
            <div class="navigation-item primary-nav-header">
                <vf-logo class="logo-small" />
                <span class="site-name truncate">
                    {{ siteName }}
                </span>
            </div>
            <nav
                id="primary-nav-links"
                class="navigation-item js-navigation"
                :aria-label="$i18n('accessibility.sections.uh_nav_links')"
            >
                <ul class="primary-nav-center js-nav-links">
                    <nav-item
                        v-for="item in navItems"
                        :key="item.key"
                        :name="item.name"
                        :user="user"
                        :links="item.submenu"
                        :link="item.link"
                        :is-new="item.isNew"
                        :is-beta="item.isBeta"
                        :open-in-new-tab="item.openInNewTab"
                        :is-active="item.isActive"
                        :base-admin-link="baseAdminLink"
                        :base-console-link="baseConsoleLink"
                        :base-portal-link="basePortalLink"
                    />
                </ul>
            </nav>
            <nav
                id="primary-user-links"
                class="navigation-item primary-nav-right js-user"
                :aria-label="$i18n('accessibility.sections.uh_user_links')"
            >
                <nav-user-item
                    name="user"
                    :links="userLinks"
                    :user="user"
                    :base-admin-link="baseAdminLink"
                    @logout-clicked="$emit('logout-clicked', arguments[0])"
                />
            </nav>
        </div>
    </header>
</template>

<script>
import { BASE_LINKS, USER_LINKS } from './constants/links.js';
import { NAVIGATION } from './constants/universal_header.js';
import { canView } from './util/permissions.js';
import navItem from './nav-item.vue';
import navUserItem from './nav-user-item.vue';
import { vfLogo } from 'design-assets/components/svg/icons/logo.c.js';

export default {
    props: {
        siteName: {
            type: String,
        },
        user: {
            type: Object,
            required: true,
        },
        activeLink: {
            type: String,
            validator: (link) => Object.values(NAVIGATION).includes(link),
        },
        baseAdminLink: {
            type: String,
            required: true,
        },
        baseConsoleLink: {
            type: String,
            required: true,
        },
        basePortalLink: {
            type: String,
            required: true,
        },
    },

    data() {
        return {
            headerHeight: 0,
            resizeObserver: null,
            onResize: null,
        };
    },

    mounted() {
        this.onResize = () => this.emitHeaderHeight();
        this.emitHeaderHeight();

        if (typeof ResizeObserver !== 'undefined') {
            this.resizeObserver = new ResizeObserver(this.onResize);
            this.resizeObserver.observe(this.$el);
        }
        else if (typeof window !== 'undefined') {
            window.addEventListener('resize', this.onResize);
        }
    },

    updated() {
        this.emitHeaderHeight();
    },

    beforeDestroy() {
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
            this.resizeObserver = null;
        }
        if (this.onResize && typeof window !== 'undefined') {
            window.removeEventListener('resize', this.onResize);
        }
    },

    computed: {
        userLinks() {
            return this.getLinks(USER_LINKS);
        },

        navItems() {
            return BASE_LINKS.map((item) =>
                Object.assign(item, {
                    userId: this.user.id,
                    isActive: item.name === this.activeLink,
                })).filter((item) => item.isVisible(this.user));
        },
    },

    methods: {
        emitHeaderHeight() {
            if (!this.$el) {
                return;
            }
            const height = Math.round(this.$el.getBoundingClientRect().height);
            if (height === this.headerHeight) {
                return;
            }
            this.headerHeight = height;
            this.$emit('header-height-change', height);
        },

        getLinks(links) {
            return links
                .filter((link) => !link.shouldTestVisibility || canView(link.name, this.user))
                .map(this.processLink);
        },

        processLink(link) {
            return Object.assign(
                {
                    isActive: link.name === this.activeLink,
                    target: false,
                    desc: false,
                },
                link,
                {
                    href: link.href({
                        baseAdminLink: this.baseAdminLink,
                        baseConsoleLink: this.baseConsoleLink,
                        basePortalLink: this.basePortalLink,
                    }, this.user),
                    i18nName: `analytics.nav.${link.name}`,
                    uid: this.user.id,
                },
            );
        },
    },

    components: {
        navItem,
        navUserItem,
        vfLogo,
    },
};
</script>

<style lang="scss">
@import 'design-assets/css/variables/module';

$header-padding: 1.1em; // ~22px
$header-padding-mobile: 0.3em;
$header-border-width: 0;

.truncate {
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;

    // IE requires that this property be set on the element containing the text node
    & * {
        white-space: nowrap;
    }
}

.primary-navigation .vf-tooltip-content .menu-link {
    display: block;
    text-align: center;
}

.primary-navigation .btn-link {
    margin: inherit;
    padding: inherit;
    color: inherit;
    font-weight: inherit;
    font-family: inherit;
    background: none;
    border: none;

    &[disabled] {
        cursor: not-allowed;
    }
}

/** Styling **/
.primary-navigation .head,
.primary-navigation .site-name {
    font-size: map-get($font-sizes, large);
}

.primary-navigation,
.primary-nav-header {
    @include var(background-color, 'primary-color-100');
    @include var(color, 'primary-color-30');
}

.primary-navigation li,
.primary-navigation .btn-link {
    position: relative;
    list-style-type: none;
    cursor: default;
}

.primary-navigation .btn-link.head-link[disabled] {
    @include var(color, 'primary-color-30');
}

/* stylelint-disable-next-line no-duplicate-selectors */
.primary-navigation .btn-link[disabled] {
    @include var(color, 'primary-color-30');
}

.primary-navigation a,
.primary-navigation .btn-link {
    @include var(color, 'text-on-primary-color');

    text-decoration: none;
}

.primary-navigation .vf-tooltip .menu-link {
    @include light-text($default-palette);

    padding: 0.5em;
    font-weight: 300;
    font-size: map-get($font-sizes, regular);

    &:hover {
        font-weight: 300;
    }
}

.primary-navigation {
    li.vf-active a,
    a:not([disabled]):hover,
    a:not([disabled]):focus,
    .vf-active,
    li.vf-active button,
    li.vf-active:hover a,
    li.vf-active:hover button {
        @include var(color, 'text-on-primary-color');
    }

    .vf-tooltip {
        li.vf-active a,
        a:not([disabled]):hover,
        a:not([disabled]):focus,
        .vf-active,
        li.vf-active button,
        li.vf-active:hover a,
        li.vf-active:hover button {
            @include dark-text($default-palette);

            opacity: 1;
        }
    }
}

.primary-navigation .vf-expanding-hover-light::before {
    border-color: $light-red;

    // The border width should be 2px in all cases except in a tab menu
    border-width: 3px;
}

.primary-nav-header .site-name {
    @include var(color, 'text-on-primary-color');

    font-weight: 200;
    line-height: normal;
}

.primary-navigation .user:hover,
.primary-nav-center > li:hover {
    @include var(background-color, 'primary-color-60');
}

.primary-navigation .user:hover,
.primary-nav-center > li:hover .head {
    @include var(color, 'text-on-primary-color');

    .vf-tooltip & {
        @include dark-text($default-palette);
    }
}

.primary-navigation .vf-tooltip-content {
    @include var(color, 'primary-color-60');

    min-width: 10em;
    font-weight: 300;
    text-align: center;
}

.primary-navigation-menu.tooltip.bottom {
    top: 100%;
}

.primary-nav-header .logo-small {
    width: 20px;
    height: 20px;
}

.primary-navigation .vf-dropdown > .vf-tooltip {
    min-width: initial;

    // Override styles from Front which is also on the page
    background-color: initial;
}

/** Layout **/
.primary-navigation * {
    box-sizing: border-box;
}

/* stylelint-disable-next-line no-descending-specificity */
.primary-navigation .head,
.primary-nav-center {
    min-height: calc(#{$header-padding} * 2 + 1.3em);
}

.primary-nav-center {
    display: flex;

    // This is to account for the ul/ol also having 10px margin bottom
    margin: 0;

    /* stylelint-disable-next-line no-descending-specificity */
    & > li {
        position: relative;
        display: inline-block;
    }
}

/* stylelint-disable-next-line no-duplicate-selectors */
.primary-navigation {
    min-width: 100%;
    max-width: 100%;

    /**
    These values are weird because we can't set the font-size on the
    .primary-navigation element because not all children are the same font
    size and because of using ems, we can't set font-size multiple times

    Possible work around using font-size: initial on a child somewhere to reset
    things could work.
    */
    max-height: 4.1em; // TODO: figure out a better option for this.

    .primary-nav-right {
        position: relative;
    }
}

.no-scroll {
    position: fixed;
    overflow: hidden;
}

.no-scroll .primary-navigation .header-container {
    position: fixed;
    padding-top: calc(20px + #{$header-padding});
    overflow-y: scroll;
}

/* stylelint-disable-next-line no-descending-specificity */
.primary-navigation .header-container {
    display: flex;
    width: 100%;
    padding: 0 0.65em; // ~30px when included with the padding on the inner items
}

/* stylelint-disable-next-line no-descending-specificity */
.primary-nav-header,
.primary-nav-right {
    display: flex;
    flex: 1 0 140px;
    align-items: flex-start;
    justify-content: flex-end;
    padding-left: 1em;
}

.primary-nav-right .head.btn-link {
    min-width: calc(10em + 40px);
}

.header-container .primary-link {
    display: flex;
    flex-direction: column;
}

.user .icon-cog {
    display: block;
    width: 21px;
    height: 21px;
    margin-left: 10px;
}

/* stylelint-disable-next-line no-descending-specificity */
.primary-navigation .head {
    display: flex;
    align-items: center;

    // to align the text on ie11
    height: calc(#{$header-padding} * 2 + 1.3em);
    margin: auto;
    padding-right: 1em;
    padding-left: 1em;
    vertical-align: middle;
}

.primary-nav-right .vf-dropdown-trigger {
    justify-content: center;
    max-width: 360px;

    & > span {
        display: flex;
        align-items: center;
    }
}

.primary-navigation .primary-nav-header {
    z-index: 102;
    display: flex;
    align-items: center;
    justify-content: flex-start;
    width: 100%;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
}

.primary-nav-header > * + * {
    margin-left: 10px;
}

/* stylelint-disable-next-line no-duplicate-selectors */
.primary-navigation * {
    // 101 to hide the Doorbell.io "feedback" button, otherwise can be 1
    z-index: 101;
}

.navigation-item__new-badge {
    margin-left: 0.5em;
}

.navigation-item__beta-badge {
    margin-left: 0.5em;
    margin-bottom: 1em;
    font-size: .75em;
    text-transform: inherit !important;
}
</style>
